'use client'

import { Monitor, Moon, Sun } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { iconStroke } from '@lib/iconProps'

export type ThemesActual = 'light' | 'light-dracula' | 'dark' | 'dark-dracula'
export type Theme = ThemesActual | 'system'

type ThemeObject = {
  name: Theme
  icon: React.ReactNode
}

export const themeIconProps = { className: '!size-4', strokeWidth: iconStroke.regular }

export const LIGHT_THEMES: Array<ThemeObject> = [{ name: 'light', icon: <Sun {...themeIconProps} /> }]
export const DARK_THEMES: Array<ThemeObject> = [{ name: 'dark', icon: <Moon {...themeIconProps} /> }]
export const SYSTEM_THEMES: Array<ThemeObject> = [{ name: 'system', icon: <Monitor {...themeIconProps} /> }]

const THEMES_WITH_ICONS: Array<ThemeObject> = [...SYSTEM_THEMES, ...LIGHT_THEMES, ...DARK_THEMES]
const THEMES: Array<Theme> = THEMES_WITH_ICONS.map((t) => t.name)

const THEME_STORAGE_KEY = 'theme-preference'
const SPARKLE_STORAGE_KEY = 'sparkle-preference'

const getThemeLS = (): Theme | null => {
  if (typeof window === 'undefined') return null
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null

    return v && THEMES.includes(v) ? v : null
  } catch {
    return null
  }
}
const getSparkleLS = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    const v = JSON.parse(localStorage.getItem(SPARKLE_STORAGE_KEY) as string) as boolean | null

    return v ?? false
  } catch {
    return false
  }
}

const resolveActualTheme = (theme: Theme, isDarkOs: boolean, isSparkle: boolean): ThemesActual => {
  if (theme === 'system') {
    if (isSparkle) {
      return isDarkOs ? 'dark-dracula' : 'light-dracula'
    }

    return isDarkOs ? 'dark' : 'light'
  }

  if (isSparkle) {
    return theme === 'dark' ? 'dark-dracula' : 'light-dracula'
  }

  return theme
}

export function ThemeProvider({ children, initialTheme }: { children: ReactNode; initialTheme?: Theme }) {
  // SSR-safe defaults; real preference is read from localStorage after mount (avoids hydration mismatch)
  const serverTheme = initialTheme || 'system'
  const [theme, setTheme] = useState<Theme>(serverTheme)
  const [icon, setIcon] = useState<React.ReactNode>(THEMES_WITH_ICONS.find((t) => t.name === serverTheme)?.icon)
  const [isSparkle, setIsSparkle] = useState<boolean>(false)
  const [isDarkOS, setIsDarkOS] = useState<boolean>(false)

  useEffect(() => {
    const stored = getThemeLS()
    if (stored) setTheme(stored)
    setIsSparkle(getSparkleLS())
  }, [])

  useEffect(() => {
    const root = document.documentElement
    const isDarkOS = window.matchMedia?.('(prefers-color-scheme: dark)')
    const isDarkOSMatches = !!isDarkOS?.matches
    setIsDarkOS(isDarkOSMatches)

    const apply = (preferTheme: Theme) => {
      const actualTheme = resolveActualTheme(preferTheme, isDarkOSMatches, isSparkle)

      root.setAttribute('data-theme', actualTheme)

      // UI icon: show the selected preference (system => Monitor icon)
      setIcon(THEMES_WITH_ICONS.find((t) => t.name === preferTheme)?.icon)

      try {
        localStorage.setItem(THEME_STORAGE_KEY, preferTheme)
        localStorage.setItem(SPARKLE_STORAGE_KEY, JSON.stringify(isSparkle))
      } catch {
        // ignore
      }
    }

    apply(theme)

    // When preference is "system", keep it in sync when OS theme changes.
    if (theme === 'system' && isDarkOS) {
      const onChange = () => apply('system')

      if (isDarkOS.addEventListener) isDarkOS.addEventListener('change', onChange)

      return () => {
        if (isDarkOS.removeEventListener) isDarkOS.removeEventListener('change', onChange)
      }
    }

    return
  }, [isSparkle, theme])

  const toggle = useCallback(() => setTheme((t) => THEMES[(THEMES.indexOf(t) + 1) % THEMES.length] as Theme), [])
  const toggleSparkle = useCallback(() => setIsSparkle((s) => !s), [])

  const values = useMemo(
    () => ({ theme, isDarkOS, icon, isSparkle, setTheme, toggle, toggleSparkle }),
    [theme, isDarkOS, icon, isSparkle, toggle, toggleSparkle],
  )

  return <ThemeContext.Provider value={values}>{children}</ThemeContext.Provider>
}

const ThemeContext = createContext<{
  theme: Theme
  isDarkOS: boolean
  icon: React.ReactNode
  isSparkle: boolean
  setTheme: (t: Theme) => void
  toggle: () => void
  toggleSparkle: () => void
}>({
  theme: THEMES_WITH_ICONS[0]!.name,
  isDarkOS: false,
  icon: THEMES_WITH_ICONS[0]!.icon,
  isSparkle: false,
  setTheme: () => {},
  toggle: () => {},
  toggleSparkle: () => {},
})

export const useTheme = () => useContext(ThemeContext)
